import { RefObject, useCallback, useEffect, useRef, useState } from "react";
import debounce from "lodash/debounce";

import { useTranslationContext } from "context/TranslationContext";
import { formatJapaneseEraNotation } from "helpers/japaneseEra";
import { isDigitKey } from "helpers/keyboardKeys";
import cn from "lib/cn";

import { TInputVariant } from "./types";

interface DateInputProps {
  id?: string;
  startDate?: Date;
  endDate?: Date;
  isSelectRange?: boolean;
  isError?: boolean;
  label?: string;
  labelClassName?: string;
  className?: string;
  showJapaneseEra?: boolean;
  onChange?: (startDateString: string, endDateString: string) => void;
}

const DateInput = ({
  id,
  startDate,
  endDate,
  isSelectRange,
  isError,
  labelClassName,
  className,
  label,
  showJapaneseEra = true,
  onChange,
}: DateInputProps) => {
  const { t } = useTranslationContext();
  const [tmpStartDate, setTmpStartDate] = useState("");
  const [tmpEndDate, setTmpEndDate] = useState("");

  const handleChangeStart = (dateString: string) => {
    setTmpStartDate(dateString);
    if (onChange) {
      onChange(dateString, tmpEndDate);
    }
  };

  const handleChangeEnd = (dateString: string) => {
    setTmpEndDate(dateString);
    if (onChange) {
      onChange(tmpStartDate, dateString);
    }
  };

  useEffect(() => {
    if (startDate) {
      setTmpStartDate(
        `${startDate.getFullYear()}-${`${startDate.getMonth() + 1}`.padStart(2, "0")}-${`${startDate.getDate()}`.padStart(2, "0")}`
      );
    }
  }, [startDate]);

  useEffect(() => {
    if (endDate) {
      setTmpEndDate(
        `${endDate.getFullYear()}-${`${endDate.getMonth() + 1}`.padStart(2, "0")}-${`${endDate.getDate()}`.padStart(2, "0")}`
      );
    }
  }, [endDate]);

  const { language } = useTranslationContext();
  const eraCode =
    showJapaneseEra && startDate
      ? formatJapaneseEraNotation(startDate, language)
      : "";

  return (
    <div className={cn(className, "w-full")}>
      {label && (
        <div className={cn(labelClassName, "text-gray-500 text-base")}>
          {label}
        </div>
      )}
      <div className="flex items-center gap-3">
        <div
          className={cn(
            "flex-1 border rounded-lg px-3 py-2",
            isError ? "border-red-500" : "border-gray-200"
          )}
        >
          <div className="w-full flex items-center justify-start gap-2">
            <div>
              <DateComponent
                id={id ? `${id}-start` : undefined}
                date={startDate}
                onChange={handleChangeStart}
              />
            </div>
            {isSelectRange && (
              <>
                <div className="text-base align-text-bottom">-</div>
                <div>
                  <DateComponent
                    id={id ? `${id}-end` : undefined}
                    date={endDate}
                    onChange={handleChangeEnd}
                  />
                </div>
              </>
            )}
          </div>
        </div>
        {eraCode && (
          <span className="px-2 py-2.5 bg-gray-100 text-gray-700 text-base rounded-lg border border-gray-300 font-medium">
            {eraCode}
          </span>
        )}
      </div>

      {isError && (
        <div className="text-sm text-red-500"> {t("invalid_date")} </div>
      )}
    </div>
  );
};

export default DateInput;

interface DateComponentProps {
  id?: string;
  date?: Date;
  onChange?: (dateString: string) => void;
}

const DateComponent = ({ id, date, onChange }: DateComponentProps) => {
  const { t } = useTranslationContext();
  const inputYearRef = useRef<HTMLSpanElement>(null);
  const inputMonthRef = useRef<HTMLSpanElement>(null);
  const inputDayRef = useRef<HTMLSpanElement>(null);
  const [year, setYear] = useState("");
  const [month, setMonth] = useState("");
  const [day, setDay] = useState("");

  const handleChangeInput =
    (inputType: TInputVariant) => (e: React.ChangeEvent<HTMLSpanElement>) => {
      switch (inputType) {
        case "year":
          if ((e.target.textContent?.length || 0) >= 4) {
            inputMonthRef.current?.focus();
            inputMonthRef.current?.click();
          } else {
            inputYearRef.current?.blur();
          }
          break;
        case "month":
          if ((e.target.textContent?.length || 0) >= 2) {
            inputDayRef.current?.focus();
            inputDayRef.current?.click();
          } else {
            inputMonthRef.current?.blur();
          }
          break;
        case "day":
          inputDayRef.current?.blur();
          inputDayRef.current?.click();
          break;
      }
    };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleDebounceChangeInput = useCallback(
    debounce(
      (e: React.FormEvent<HTMLSpanElement>, inputType: TInputVariant) => {
        handleChangeInput(inputType)(e as React.ChangeEvent<HTMLSpanElement>);
      },
      800
    ),
    []
  );

  const handleChangeInputWrapper =
    (inputType: TInputVariant) => (e: React.ChangeEvent<HTMLSpanElement>) => {
      const maxLength = inputType === "year" ? 4 : 2;
      if ((e?.target?.textContent?.length || 0) >= maxLength) {
        handleChangeInput(inputType)(e);
      } else {
        handleDebounceChangeInput(e, inputType);
      }
    };

  const handleOnClick = (ref?: RefObject<HTMLSpanElement>) => () => {
    if (!ref?.current) return;
    const range = document.createRange();
    range.selectNodeContents(ref.current);

    const selection = window.getSelection();
    selection?.removeAllRanges();
    selection?.addRange(range);
  };

  const handleOnFocus = (ref?: RefObject<HTMLSpanElement>) => () => {
    ref?.current?.click();
  };

  const handleOnKeyDown =
    (
      nextRef: RefObject<HTMLSpanElement>,
      prevRef: RefObject<HTMLSpanElement>
    ) =>
    (e: React.KeyboardEvent<HTMLSpanElement>) => {
      switch (e.code) {
        case "ArrowLeft":
          prevRef.current?.focus();
          prevRef.current?.click();
          e.preventDefault();
          break;
        case "ArrowRight":
        case "Tab":
          nextRef.current?.focus();
          nextRef.current?.click();
          e.preventDefault();
          break;
        default:
          if (isDigitKey(e.code) || ["Backspace"].includes(e.code)) {
            return;
          } else {
            e.preventDefault();
          }
          break;
      }
    };

  const handleBlurInput =
    (inputType: TInputVariant) => (e: React.ChangeEvent<HTMLSpanElement>) => {
      let value = e?.target?.textContent || "";
      let tmpDay = day;
      let tmpMonth = month;
      let tmpYear = year;

      switch (inputType) {
        case "year": {
          if (e?.target?.textContent && Number(e.target.textContent) > 9999) {
            value = "9999";
          }
          value = value.padStart(4, "0");
          setYear(value);
          tmpYear = value;
          if (inputYearRef?.current) {
            inputYearRef.current.textContent = value;
          }

          inputMonthRef.current?.focus();
          inputMonthRef.current?.click();
          break;
        }
        case "month": {
          if (e?.target?.textContent && Number(e.target.textContent) > 12) {
            value = "12";
          }
          value = value.padStart(2, "0");
          tmpMonth = value;
          setMonth(value);
          if (inputMonthRef?.current) {
            inputMonthRef.current.textContent = value;
          }

          inputDayRef.current?.focus();
          inputDayRef.current?.click();
          break;
        }
        case "day": {
          if (e?.target?.textContent && Number(e.target.textContent) > 31) {
            value = "31";
          }
          value = value.padStart(2, "0");
          tmpDay = value;
          setDay(value);
          if (inputDayRef?.current) {
            inputDayRef.current.textContent = value;
          }
          break;
        }
      }

      if (tmpDay && tmpMonth && tmpYear && onChange) {
        onChange(`${tmpYear}-${tmpMonth}-${tmpDay}`);
      }
    };

  useEffect(() => {
    if (date) {
      setYear(`${date.getFullYear()}`.padStart(4, "0"));
      setMonth(`${date.getMonth() + 1}`.padStart(2, "0"));
      setDay(`${date.getDate()}`.padStart(2, "0"));
    }
  }, [date]);

  return (
    <span data-sectionindex="0">
      <span
        ref={inputYearRef}
        id={id ? `${id} - dateinput-year` : undefined}
        role="spinbutton"
        aria-labelledby={id ? `${id} - dateinput-year` : undefined}
        aria-readonly={false}
        aria-valuemin={0}
        aria-valuemax={9999}
        aria-label="Year"
        aria-disabled={false}
        tabIndex={0}
        spellCheck={false}
        autoCapitalize="off"
        autoCorrect="off"
        inputMode="numeric"
        className="focus:outline-none text-base align-text-bottom"
        contentEditable
        suppressContentEditableWarning
        onKeyDown={handleOnKeyDown(inputMonthRef, inputYearRef)}
        onInput={handleChangeInputWrapper("year")}
        onClick={handleOnClick(inputYearRef)}
        onFocus={handleOnFocus(inputYearRef)}
        onBlur={handleBlurInput("year")}
      >
        {Number(year) ? year : t("date_yyyy")}
      </span>
      <span className="text-base align-text-bottom">/</span>
      <span
        ref={inputMonthRef}
        id={id ? `${id} - dateinput-month` : undefined}
        role="spinbutton"
        aria-labelledby={id ? `${id} - dateinput-month` : undefined}
        aria-readonly={false}
        aria-valuemin={1}
        aria-valuemax={12}
        aria-label="Month"
        aria-disabled={false}
        tabIndex={0}
        spellCheck={false}
        autoCapitalize="off"
        autoCorrect="off"
        inputMode="numeric"
        className="focus:outline-none text-base align-text-bottom"
        contentEditable
        suppressContentEditableWarning
        onKeyDown={handleOnKeyDown(inputDayRef, inputYearRef)}
        onInput={handleChangeInputWrapper("month")}
        onClick={handleOnClick(inputMonthRef)}
        onFocus={handleOnFocus(inputMonthRef)}
        onBlur={handleBlurInput("month")}
      >
        {Number(month) ? month : t("date_mm")}
      </span>
      <span className="text-base align-text-bottom">/</span>
      <span
        ref={inputDayRef}
        id={id ? `${id} - dateinput-day` : undefined}
        role="spinbutton"
        aria-labelledby={id ? `${id} - dateinput-day` : undefined}
        aria-readonly={false}
        aria-valuemin={1}
        aria-valuemax={32}
        aria-label="Day"
        aria-disabled={false}
        tabIndex={0}
        spellCheck={false}
        autoCapitalize="off"
        autoCorrect="off"
        inputMode="numeric"
        className="focus:outline-none text-base align-text-bottom"
        contentEditable
        suppressContentEditableWarning
        onKeyDown={handleOnKeyDown(inputDayRef, inputMonthRef)}
        onInput={handleChangeInputWrapper("day")}
        onClick={handleOnClick(inputDayRef)}
        onFocus={handleOnFocus(inputDayRef)}
        onBlur={handleBlurInput("day")}
      >
        {Number(day) ? day : t("date_dd")}
      </span>
    </span>
  );
};
